/**
 * Order module — service layer.
 *
 * Bridges UI to the repository and to Cloud Functions. Components and hooks
 * call this; this calls into `repositories` and `httpsCallable`. No
 * component should import Firestore or Functions SDKs directly.
 *
 * The order lifecycle state machine lives in `./lifecycle.ts`. Every
 * client-side status-mutating write routes through `updateOrder`, which
 * gates patches via `canTransition`; the event-driven auto-advances
 * (deposit received, MC complete, promotion) run server-side in the Cloud
 * Functions. The old client-side `advanceOrderStatus` / `createSyntheticOrder`
 * helpers lost their last callers when the offline mirrors were deleted
 * (dual-path retirement) and were removed with them.
 */

import { repositories } from '../../../data/repositories';
import { allocateSequenceNumber } from '../../../lib/sequenceAllocator';
import type { Order, OrderStatus, EntityId } from '../../../types';
import { customerService } from '../../customers/services/customerService';
import { canTransition } from './lifecycle';
import { normalizeMobile } from '../../../../shared/phone';
import { byCreatedDesc } from '../../../utils/sort';

/**
 * Persist an order via the repository. The localStorage mirror (with its
 * `mock_order_` / legacy `mock_project_` dual-prefix read) was deleted in the
 * dual-path retirement (Phase 2b) — local dev uses the emulator. The prod
 * orders-collection dual-read (legacy `projectNumber` normalization) lives in
 * the repository layer.
 */
const persistOrder = async (order: Order): Promise<void> => {
  await repositories.orders.save(order);
};

export interface CreateOrderInput {
  customer: Order['customer'];
  issuingCompany: Order['issuingCompany'];
  assignedStaff: string[];
  notes?: string;
  createdBy: string;
}

export const createOrder = async (input: CreateOrderInput): Promise<Order> => {
  // The OC number IS the order number (spec 2026-09-09 entity-prefixed
  // numbering §4.3): one family serial per order, `{L}OC-YYYY-NNNN`, written
  // to both orderNumber and ocNumber. The doc-id prefix stays `prj_` (opaque).
  const { number: orderNumber } = await allocateSequenceNumber('OC', input.issuingCompany);
  const id = `prj_${crypto.randomUUID()}`;
  const now = new Date().toISOString();

  const order: Order = {
    id,
    orderNumber,
    ocNumber: orderNumber,
    status: 'AWAITING_DEPOSIT',
    customer: input.customer,
    issuingCompany: input.issuingCompany,
    assignedStaff: input.assignedStaff,
    documentRefs: {
      quoteIds: [],
      purchaseOrderIds: [],
      workOrderIds: [],
      deliveryOrderIds: [],
      measurementConfirmationIds: [],
    },
    notes: input.notes,
    createdAt: now,
    createdBy: input.createdBy,
    updatedAt: now,
    updatedBy: input.createdBy,
  };

  await persistOrder(order);
  await customerService.syncCustomerFromEntity(order.customer);
  return order;
};

export const fetchOrder = async (id: EntityId): Promise<Order | null> => {
  return repositories.orders.findById(id);
};

export const listOrders = async (filter?: {
  status?: OrderStatus[];
  assignedTo?: string;
}): Promise<Order[]> => {
  const orders = await repositories.orders.list(filter);
  return orders.sort(byCreatedDesc);
};

/**
 * Orders belonging to one customer record, for the customer-detail page.
 * READ-ONLY display aggregation: order reads are open to any internal user
 * (firestore.rules `/orders` allow read: isInternalUser), so we list all and
 * filter by the customer's normalized mobile (= the customer doc id)
 * client-side. This is **not** order linking — mobile must never decide which
 * order a quote belongs to (Lesson #45 / orders CLAUDE.md); this only surfaces
 * "orders for this customer record", where the shared-mobile caveat is inherent
 * to the mobile-keyed customer model.
 */
export const listOrdersForCustomer = async (mobile: string): Promise<Order[]> => {
  const target = normalizeMobile(mobile);
  if (!target) return [];
  const all = await repositories.orders.list();
  return all
    .filter((o) => normalizeMobile(o.customer?.mobile ?? '') === target)
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
};

export const updateOrder = async (
  id: EntityId,
  patch: Partial<Omit<Order, 'id' | 'orderNumber' | 'createdAt' | 'createdBy' | 'customer'>>,
  actor: string,
): Promise<Order> => {
  const current = await fetchOrder(id);
  if (!current) throw new Error(`Order ${id} not found.`);

  // Backward-status writes are blocked at the service layer regardless of
  // what the form supplied — the admin Edit form's dropdown enumerates every
  // status, including `OPEN` for an order already in `IN_PRODUCTION`. The
  // lifecycle module is the single source of truth.
  if (patch.status && patch.status !== current.status) {
    if (!canTransition(current.status, patch.status)) {
      throw new Error(
        `Cannot move order from ${current.status} to ${patch.status}. ` +
          `Status moves forward through the lifecycle; the only exits are CANCELLED or ON_HOLD.`,
      );
    }
  }

  // `customer` is not patchable here: after an order exists it is written only
  // by the amendOrderCustomer Cloud Function, and the /orders rule refuses a
  // client change to it (post-confirmation amendment, council B4). The
  // re-read above carries the stored map through this full-document save
  // unchanged, so it passes the rule's equality lock.
  const updated: Order = {
    ...current,
    ...patch,
    updatedAt: new Date().toISOString(),
    updatedBy: actor,
  };

  await persistOrder(updated);
  return updated;
};
